import { useEffect, useState, FormEvent } from "react"
import apiClient from "../../lib/api"
import type { IAmenClickStats, IWeeklyDeclaration } from "@dc-ireland/shared"
import AmenClickStats from "../../components/admin/AmenClickStats"
import ImagePicker from "../../components/admin/ImagePicker"
import AdminAlerts from "../../components/admin/AdminAlerts"

const emptyForm: Omit<IWeeklyDeclaration, "id"> = {
  highlight: "",
  fullDeclaration: "",
  imageUrl: "",
  scripture: "",
  scriptureReference: "",
}

const AdminWeeklyDeclaration = () => {
  const [form, setForm] = useState(emptyForm)
  const [stats, setStats] = useState<IAmenClickStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        const [declaration, amenStats] = await Promise.all([
          apiClient.getWeeklyDeclaration(),
          apiClient.getAmenClickStats(),
        ])
        if (declaration) {
          setForm({
            highlight: declaration.highlight,
            fullDeclaration: declaration.fullDeclaration,
            imageUrl: declaration.imageUrl,
            scripture: declaration.scripture ?? "",
            scriptureReference: declaration.scriptureReference ?? "",
          })
        }
        setStats(amenStats)
      } catch (err) {
        console.error(err)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await apiClient.updateWeeklyDeclaration(form)
      setSuccess("Weekly declaration updated.")
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update weekly declaration")
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <span className="loading loading-spinner loading-md"></span>
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">Weekly Declaration</h2>
        <p className="mt-1 text-secondary">
          Manage the weekly declaration shown on the landing page.
        </p>
      </div>

      {stats && <AmenClickStats stats={stats} />}

      <AdminAlerts
        error={error}
        success={success}
        onClearError={() => setError(null)}
        onClearSuccess={() => setSuccess(null)}
      />

      <form onSubmit={handleSubmit} className="card bg-base-100 shadow-xl">
        <div className="card-body space-y-4">
          <label className="form-control">
            <span className="label-text">Highlighted phrase</span>
            <span className="label-text-alt text-xs">
              Shown in gold within the heading (e.g. &quot;unlimited favor&quot;)
            </span>
            <input
              className="input input-bordered"
              value={form.highlight}
              onChange={(e) => setForm({ ...form, highlight: e.target.value })}
              placeholder="unlimited favor"
              required
            />
          </label>
          <label className="form-control">
            <span className="label-text">Full declaration</span>
            <span className="label-text-alt text-xs">
              Shown in the modal. Use blank lines between paragraphs.
            </span>
            <textarea
              className="textarea textarea-bordered"
              rows={8}
              value={form.fullDeclaration}
              onChange={(e) => setForm({ ...form, fullDeclaration: e.target.value })}
              required
            />
          </label>
          <label className="form-control">
            <span className="label-text">Bible verse</span>
            <span className="label-text-alt text-xs">
              Shown under the declaration heading on the landing page and in the modal.
            </span>
            <textarea
              className="textarea textarea-bordered"
              rows={3}
              value={form.scripture}
              onChange={(e) => setForm({ ...form, scripture: e.target.value })}
              placeholder="For you bless the righteous, O Lord; you cover him with favor as with a shield."
            />
          </label>
          <label className="form-control">
            <span className="label-text">Scripture reference</span>
            <input
              className="input input-bordered"
              value={form.scriptureReference}
              onChange={(e) => setForm({ ...form, scriptureReference: e.target.value })}
              placeholder="Psalm 5:12"
            />
          </label>
          <ImagePicker
            label="Section image"
            value={form.imageUrl}
            onChange={(imageUrl) => setForm({ ...form, imageUrl })}
            hint="Shown on the right of the section on desktop."
            required
          />
          <div className="card-actions justify-end">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Declaration"}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

export default AdminWeeklyDeclaration
